<script setup lang="ts">
import { ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import AppButton from '@uiKit/elements/Button.vue'
import AppInput from '@uiKit/elements/Input.vue'
import AppMessage from '@uiKit/elements/Message.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import TokenConnectPlaceholder from './TokenConnectPlaceholder.vue'

interface Props {
  loading?: boolean
}

withDefaults(defineProps<Props>(), { loading: false })

const emit = defineEmits<{
  (e: 'connect', address: string): void
}>()

const walletAddress = ref('')
const clientError = ref('')

watch(walletAddress, () => {
  if (clientError.value) clientError.value = ''
})

const pasteFromClipboard = async () => {
  try {
    const text = await navigator.clipboard.readText()
    walletAddress.value = text.trim()
  } catch {
    // clipboard access denied — no-op
  }
}

const handleConnect = () => {
  const value = walletAddress.value.trim()
  if (!value) {
    clientError.value = 'Enter or paste an XRPL wallet address to continue.'
    return
  }
  clientError.value = ''
  emit('connect', value)
}

const steps = [
  {
    number: 1,
    title: 'Connect Wallet',
    description: 'Link your existing crypto wallet address.',
  },
  {
    number: 2,
    title: 'Activate Wallet',
    description: 'Ensure wallet is active on the XRP Ledger',
  },
  {
    number: 3,
    title: 'Enable Trustline',
    description: 'Set up trustline to receive ARVA property tokens.',
  },
]
</script>

<template>
  <Box align="start" gap="6" class="tws">
    <TokenConnectPlaceholder
      title="Connect Your Wallet"
      description="Link your existing crypto wallet to start investing and receiving ARVA property tokens."
    />

    <Box direction="col" gap="2" grow class="tws__right">
      <Box direction="col" gap="3" class="tws__address-card">
        <Text type="body" weight="semibold" is="label" class="tws__address-label">
          Wallet Address
        </Text>
        <AppInput
          v-model="walletAddress"
          placeholder="Link to your wallet address"
          class="tws__address-input"
        />
        <Box
          is="button"
          align="center"
          gap="2"
          class="tws__paste-btn"
          type="button"
          aria-label="Paste from Clipboard"
          @click="pasteFromClipboard"
        >
          <PiIcon name="copy" class="tws__paste-icon" />
          <Text type="body" weight="medium" is="span">Paste from Clipboard</Text>
        </Box>
        <Box align="start" gap="2" class="tws__info-banner">
          <PiIcon name="info-circle" class="tws__info-icon" />
          <Text type="caption" weight="medium" color="muted" class="tws__info-text">
            Only XRP Ledger (XRPL) wallet addresses are supported. Do not enter private keys or
            seed phrases.
          </Text>
        </Box>
      </Box>

      <Box direction="col" gap="2" class="tws__steps">
        <Box v-for="step in steps" :key="step.number" align="center" gap="3" class="tws__step">
          <Text type="body" weight="semibold" align="center" is="div" class="tws__step-badge">
            {{ step.number }}
          </Text>
          <Box direction="col" gap="1" class="tws__step-info">
            <Text type="heading" is="span" class="tws__step-title">{{ step.title }}</Text>
            <Text type="caption" weight="medium" color="muted" is="span">
              {{ step.description }}
            </Text>
          </Box>
        </Box>
      </Box>

      <AppMessage v-if="clientError" color="danger" variant="outlined">
        {{ clientError }}
      </AppMessage>

      <AppButton
        label="Connect Wallet"
        size="large"
        block
        :loading="loading"
        @click="handleConnect"
      />
    </Box>
  </Box>
</template>

<style scoped>
.tws {
  width: 100%;
}

.tws__address-card {
  background: var(--p-surface-0);
  border-radius: var(--p-border-radius-lg);
  padding: 1.25rem;
}

.tws__address-label {
  line-height: 20px;
}

.tws__address-input {
  width: 100%;
}

.tws__address-input :deep(.p-inputtext) {
  width: 100%;
  border-radius: var(--p-border-radius-md) !important;
}

.tws__paste-btn {
  display: inline-flex;
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-md);
  padding: 0.625rem 1rem;
  color: var(--p-text-muted-color);
  cursor: pointer;
  align-self: flex-start;
  transition: background 0.15s ease;
}

.tws__paste-btn:hover {
  background: var(--p-surface-50);
}

.tws__paste-btn:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: 2px;
}

.tws__paste-icon {
  font-size: 14px;
  color: var(--p-text-muted-color);
}

.tws__info-banner {
  background: var(--p-surface-100);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-md);
  padding: 0.625rem 0.875rem;
}

.tws__info-icon {
  font-size: 14px;
  color: var(--p-info-600);
  flex-shrink: 0;
  margin-top: 2px;
}

.tws__info-text {
  line-height: 18px;
  margin: 0;
}

.tws__steps {
  background: var(--p-surface-0);
  border-radius: var(--p-border-radius-lg);
  padding: 1.25rem;
}

.tws__step {
  background: var(--p-surface-0);
  border: 1px solid var(--p-surface-100);
  border-radius: var(--p-border-radius-lg);
  padding: 18px 1.25rem;
}

.tws__step-badge {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  background: var(--p-info-50);
  border: 1px solid var(--p-info-200);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--p-text-muted-color);
  flex-shrink: 0;
}

.tws__step-title {
  line-height: 24px;
}

@media (max-width: 992px) {
  .tws {
    flex-direction: column;
    gap: 1.25rem;
  }
}
</style>
